
import { Location } from "@angular/common";
import { Component, inject, LOCALE_ID, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { Markets, SessionKey } from "@shared/clickar-constants";
import { InformativeDialogComponent } from "@shared/dialogs/informative-dialog/informative-dialog.component";
import { Dealer } from "@shared/model/dealer.model";
import { AlertMessage } from "@shared/services/alert-message/alert-message.model";
import { CodeTable, CT } from "@shared/services/code-table/code-table.model";
import { CodeTableService } from "@shared/services/code-table/code-table.service";
import { DealerService } from "@shared/services/dealers/dealer.service";
import { SnackbarService } from "@shared/services/snackbar.service";
import { StorageManagerService } from "@shared/services/storage/storage-manager.service";
import { UserApiService } from "@shared/services/users/userApi.service";
import { ClickarUtilsService } from "@shared/services/utils/clickar-utils.service";
import { WcmService } from "@shared/services/wcm/wcm.service";
import { defineLocale, plLocale, ptBrLocale } from "ngx-bootstrap/chronos";
import { BsLocaleService } from "ngx-bootstrap/datepicker";
import { deLocale, enGbLocale, esLocale, frLocale, itLocale, nlBeLocale, nlLocale } from "ngx-bootstrap/locale";

defineLocale("it", itLocale);
defineLocale("de", deLocale);
defineLocale("fr", frLocale);
defineLocale("es", esLocale);
defineLocale("nl", nlLocale);
defineLocale("en", enGbLocale);
defineLocale("be", nlBeLocale);
defineLocale("pl", plLocale);
defineLocale("pt", ptBrLocale);
defineLocale("lu", deLocale);

/**
 * @author a.lentini
 */
@Component({
	selector: "app-registration",
	templateUrl: "./registration.component.html",
	styleUrl: "./registration.component.scss",
	standalone: false
})
export class RegistrationComponent implements OnInit {
	utils = inject(ClickarUtilsService);
	codeTableService = inject(CodeTableService);
	router = inject(Router);
	private dialog = inject(MatDialog);
	dealerService = inject(DealerService);
	userApiService = inject(UserApiService);
	localeService = inject(BsLocaleService);
	translate = inject(TranslateService);
	private formBuilder = inject(UntypedFormBuilder);
	private storageService = inject(StorageManagerService);
	wcmService = inject(WcmService);
	location = inject(Location);
	locale = inject(LOCALE_ID);

	private snackBarService = inject(SnackbarService);
	countries: CodeTable[];
	selectedCountry;
	dealerActivities: CodeTable[];
	carSold: CodeTable[];
	buyingInterests: CodeTable[];
	dealer = new Dealer();
	countrySelected: string;
	requiredCheck = false;
	checkValidation = false;
	showCFError = false;
	regions: CodeTable[];
	bsConfig = {
		containerClass: "theme-RED",
		adaptivePosition: true,
		dateInputFormat: "DD/MM/YYYY"
	};
	showErrEmailNotMatch = false;
	showFormatErrFC = false;
	showFormatErrEmail = false;
	showFormatErrRefEmail = false;
	showFormatErrConfEmail = false;
	showFormatErrPostalCode = false;
	showFormatErrVat = false;
	showNotValidate = false;
	localizedCountry: any;
	visuraCamerale: any;
	docLegalRefernce: any;
	docLegalRefernce2: any;
	visuraCameraleId: any;

	docLegalRefernceId: any;

	showErrorUpVisura: any;
	showErrorUpDoc: any;

	countryOptions: { value: string; label: string }[] = [];

	regionLabel = "";
	regionOptions: { value: string; label: string }[] = [];

	dealerActivityOptions: { value: string; label: string }[] = [];

	carSoldOptions: { value: string; label: string }[] = [];

	buyingInterestOptions: { value: string; label: string }[] = [];

	validateDate = control =>
		control.value &&
			control.value.toString().toUpperCase() === "INVALID DATE"
			? { error: true }
			: null;

	registrationForm: UntypedFormGroup = this.formBuilder.group({
		businessName: new UntypedFormControl("", Validators.required),
		address: new UntypedFormControl("", Validators.required),
		country: new UntypedFormControl("", Validators.required),
		email: new UntypedFormControl("", [Validators.required, Validators.email]),
		confEmail: new UntypedFormControl("", [Validators.required, Validators.email]),
		city: new UntypedFormControl("", [Validators.required]),
		region: new UntypedFormControl(""),
		postalCode: new UntypedFormControl(""),
		vatCode: new UntypedFormControl("", Validators.required),
		mobile: new UntypedFormControl("", Validators.required),
		website: new UntypedFormControl(""),
		phone: new UntypedFormControl(""),
		regon: new UntypedFormControl(""),
		typeDealerActivity: new UntypedFormControl("", Validators.required),
		carSold: new UntypedFormControl("", Validators.required),
		buyingInterest: new UntypedFormControl("", Validators.required),
		referenceName: new UntypedFormControl("", Validators.required),
		referenceSurname: new UntypedFormControl("", Validators.required),
		referenceFiscalCode: new UntypedFormControl(""),
		referenceBirthDate: new UntypedFormControl(null, this.validateDate),
		referenceEmail: new UntypedFormControl("", [Validators.required, Validators.email]),
		referenceFCA: new UntypedFormControl(""),
		checkCCA: new UntypedFormControl(""),
		checkDati: new UntypedFormControl(""),
		checkTec: new UntypedFormControl(""),
		checkConsents1: new UntypedFormControl(false),
		checkConsents2: new UntypedFormControl(false),
		checkConsents3: new UntypedFormControl(false),
		visura: new UntypedFormControl(""),
		doc: new UntypedFormControl(""),
		doc2: new UntypedFormControl("")
	});

	ngOnInit() {
		this.localizedCountry = this.storageService.getSessionItem(
			SessionKey.GEOLOCATION_MARKET,
			this.translate.getCurrentLang().slice(3)
		);

		this.setData();
		this.translate.onLangChange.subscribe(
			(event: LangChangeEvent) => {
				if (Markets.includes(event.lang.toUpperCase())) {
					this.localeService.use(event.lang);
				} else {
					this.localeService.use("en-IT");
				}
				this.setData();
			}
		);

		this.registrationForm.patchValue(this.dealer);

		this.validationByCountry();

		// check format:
		this.registrationForm
			.get("referenceFiscalCode")
			.valueChanges.subscribe(fiscalCode => {
				this.checkFiscalCodeFormat(fiscalCode);
			});

		this.registrationForm.get("email").valueChanges
			.subscribe(email => {
				this.showFormatErrEmail = this.checkEmailFormat(email);
			});

		this.registrationForm.get("confEmail").valueChanges
			.subscribe(email => {
				this.showFormatErrConfEmail = this.checkEmailFormat(email);
			});

		this.registrationForm
			.get("referenceEmail")
			.valueChanges.subscribe(email => {
				this.showFormatErrRefEmail = this.checkEmailFormat(email);
			});

		this.registrationForm
			.get("postalCode")
			.valueChanges.subscribe(postalCode => {
				this.showFormatErrPostalCode =
					this.checkPostalCodeFormat(postalCode);
			});

		this.registrationForm.get("vatCode").valueChanges.subscribe(vat => {
			this.showFormatErrVat = this.checkVatFormat(vat);
		});
	}

	setData(): any {
		this.codeTableService
			.getCTElementsUsingGET(
				CT[CT.COUNTRY],
				this.translate.getCurrentLang().toUpperCase().slice(0, 2)
			)
			.subscribe(value => {
				this.countries = value;
				this.countryOptions = this.countries.map(c => ({
					value: c.elementCode,
					label: `${c.idMarket}.${c.groupCode}.${c.elementCode}` // translate in template
				}));
			});
		this.codeTableService
			.getCTElementsUsingGET(
				CT[CT.DEALER_ACTIVITY],
				this.translate.getCurrentLang().toUpperCase().slice(0, 2)
			)
			.subscribe(value => {
				this.dealerActivities = value;
				this.dealerActivityOptions = this.dealerActivities.map(d => ({
					value: d.elementCode,
					label: `${d.idMarket}.${d.groupCode}.${d.elementCode}` // translation handled in template
				}));
			});
		this.codeTableService
			.getCTElementsUsingGET(
				CT[CT.CAR_SOLD],
				this.translate.getCurrentLang().toUpperCase().slice(0, 2)
			)
			.subscribe(value => {
				this.carSold = value.sort((a, b) => a.ranking - b.ranking);
				this.carSoldOptions = this.carSold.map(c => ({
					value: c.elementCode,
					label: `${c.idMarket}.${c.groupCode}.${c.elementCode}` // translate in template
				}));
			});
		this.codeTableService
			.getCTElementsUsingGET(
				CT[CT.BUYING_INTEREST],
				this.translate.getCurrentLang().toUpperCase().slice(0, 2)
			)
			.subscribe(value => {
				this.buyingInterests = value;
				this.buyingInterestOptions = this.buyingInterests.map(b => ({
					value: b.elementCode,
					label: `${b.idMarket}.${b.groupCode}.${b.elementCode}` // translate in template
				}));
			});
	}

	checkFiscalCodeFormat(fiscalCode: string) {
		switch (this.registrationForm.get("country").value) {
			case "IT": {
				this.showFormatErrFC =
					!/^[a-zA-Z]{6}[0-9]{2}[a-zA-Z][0-9]{2}[a-zA-Z][0-9]{3}[a-zA-Z]$/.test(
						fiscalCode
					);
				if (!fiscalCode) {
					this.showFormatErrFC = false;
				}
				break;
			}
			case "ES": {
				this.showFormatErrFC = fiscalCode.length !== 9;
				break;
			}
			case "DE": {
				this.showFormatErrFC = false;
				break;
			}
			case "BE":
			case "FR":
			case "NL":
			case "PL":
			case "PT":
			case "EN": {
				this.showFormatErrFC = false;
				break;
			}
			default: {
				this.showFormatErrFC = fiscalCode.length > 20;
			}
		}
	}

	checkEmailFormat = (email: string) =>
		email ?
			!/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(email) :
			false;

	checkPostalCodeFormat(postalCode: string) {
		if (postalCode) {
			switch (this.registrationForm.controls.country.value) {
				case "IT":
				// falls through
				case "ES":
				// falls through
				case "DE":
				// falls through
				case "FR":
					return !/^([0-9]){5}$/.test(postalCode);
				case "BE":
					return !/^([0-9]){4}$/.test(postalCode);
				case "NL":
					return true;
				case "EN":
				// falls through
				case "PL":
					return !/^([A-Za-z0-9]){8}$/.test(postalCode);
				case "PT":
					return !/^\d{4}-\d{3}$/.test(postalCode);
			}
		}
		return false;
	}

	checkVatFormat(vat: string) {
		if (!vat) {
			return false;
		}
		switch (this.registrationForm.get("country").value) {
			case "IT":
				return !/^([0-9]){11}$/.test(vat);
			case "ES":
				return !/^([A-Za-z0-9]){9}$/.test(vat);
			case "BE":
				return !/^([0-9]){10}$/.test(vat);
			case "FR":
				return !/^([0-9]){14}$/.test(vat);
			case "DE":
				return !/^DE\d{9}$/.test(vat);
			case "NL":
				return true;
			case "EN":
			// falls through
			case "PL":
				return !/^([A-Za-z0-9]){30}$/.test(vat);
			case "PT":
				return !/^([0-9]){9}$/.test(vat);
		}
		return !/^([A-Za-z0-9]){15}$/.test(vat);
	}

	updateRegionField(selectedCountry: string) {
		// Dynamic label
		switch (selectedCountry) {
			case "IT":
			case "BE":
			case "ES":
			case "NL":
			case "PL":
				this.regionLabel = "signup.province";
				break;
			case "FR":
				this.regionLabel = "signup.department";
				break;
			case "EN":
				this.regionLabel = "signup.county";
				break;
			case "DE":
			/* this.regionLabel = 'signup.region';
			 break; */
			/* Falls through */
			default:
				this.regionLabel = "signup.region";
		}

		this.regionOptions = this.regions.map(r => ({
			value: r.elementCode,
			label: `${r.idMarket}.${r.groupCode}.${r.elementCode}`
		}));
	}

	onChangeCountry(value: string) {
		this.countrySelected = value

		this.registrationForm.get("vatCode").updateValueAndValidity();
		this.registrationForm.get("postalCode").updateValueAndValidity();
		this.registrationForm
			.get("referenceFiscalCode")
			.updateValueAndValidity();
		this.registrationForm
			.get("referenceBirthDate")
			.updateValueAndValidity();
		this.registrationForm.get("checkCCA").updateValueAndValidity();
		this.registrationForm.get("checkDati").updateValueAndValidity();
		this.registrationForm.get("checkTec").updateValueAndValidity();
		this.registrationForm.get("region").updateValueAndValidity();

		// check format
		this.checkFiscalCodeFormat(
			this.registrationForm.get("referenceFiscalCode").value
		);

		this.showFormatErrPostalCode = this.checkPostalCodeFormat(
			this.registrationForm.get("postalCode").value
		);
		this.showFormatErrVat = this.checkVatFormat(
			this.registrationForm.get("vatCode").value
		);

		this.codeTableService
			.getCTElementsUsingGET(CT[CT.COUNTRY_AREA], value.toUpperCase())
			.subscribe(r => {
				this.regions = r;
				this.updateRegionField(value);
			});
	}

	onSubmit() {
		console.log(this.registrationForm);
		if (!this.registrationForm.valid || this.showErrEmailNotMatch) {
			window.scroll(0, 0);
			this.showNotValidate = true;
			this.sectionInvalid();
		} else {
			//controllo be del cf
			let validCf = true;
			console.log(this.registrationForm);
			if (this.registrationForm.controls.country.value === "IT") {
				const birthDate: Date = new Date(
					this.registrationForm.controls.referenceBirthDate.value
				),
					dateWithoffset =
						birthDate.getTime() -
						birthDate.getTimezoneOffset() * 6e4;

				this.userApiService
					.isValidCFUsingGET(
						dateWithoffset,
						this.registrationForm.controls.referenceFiscalCode.value,
						this.registrationForm.controls.referenceName.value,
						this.registrationForm.controls.referenceSurname.value
					)
					.subscribe(value => {
						validCf = value;

						if (!validCf) {
							this.showCFError = true;
							document
								.getElementById("internalReference")
								.scrollIntoView(true);
						} else {
							this.showCFError = false;
							const files: Blob[] = [];
							files.push(this.visuraCamerale);
							files.push(this.docLegalRefernce);
							if (this.docLegalRefernce2) {
								files.push(this.docLegalRefernce2);
							}
							const names: string[] = [];
							names.push(this.visuraCamerale.name);
							names.push(this.docLegalRefernce.name);
							if (this.docLegalRefernce2) {
								names.push(this.docLegalRefernce2.name);
							}
							if (!this.registrationForm.invalid) {
								const dealer: Dealer = this.utils.deepCopy(this.registrationForm.value),
									raw = this.registrationForm.controls.referenceBirthDate.value,
									birthDate = new Date(raw);

								// force 16:00 local time
								birthDate.setHours(16, 0, 0, 0);

								const dateWithoffset = birthDate.getTime();

								dealer.referenceBirthDate = dateWithoffset.toString()
								dealer.idMarket = this.locale.substring(0, 2);
								this.dealerService
									.registerDealerUsingPOST(
										dealer,
										files,
										names
									)
									.subscribe((/* value */) => {
										this.snackBarService.show({
											titlePlaceholder: "success.registration.msg",
											// messagePlaceholder: "dialog.success.title"
										});

										this.router.navigate(["/"]);
										// TODO: vedere se lo snackbar funziona comunque dopo il navigate
									});
							}
						}
					});
			} else {
				this.showCFError = false;
				if (!this.registrationForm.invalid) {
					const dealer: Dealer = this.utils.deepCopy(
						this.registrationForm.value
					);
					dealer.idMarket = this.locale.substring(0, 2);
					this.dealerService
						.registerDealerUsingPOST(dealer, [], [])
						.subscribe((/* value */) => {
							this.snackBarService.show({
								titlePlaceholder: "success.registration.msg",
								// messagePlaceholder: "dialog.success.title"
							});

							this.router.navigate(["/"]);
							// TODO: vedere se lo snackbar funziona comunque dopo il navigate
						});
				}
			}
		}
	}

	isIvaCorrect(iva): boolean {
		if (iva == null) {
			return false;
		}
		if (iva.length == 11) {
			let checkCode = 0;
			for (let i = 0; i < iva.length - 1; i++) {
				const tmp = +iva.charAt(i);
				if (i % 2 == 0) {
					checkCode += tmp;
				} else {
					checkCode += tmp * 2 > 9 ? tmp * 2 - 9 : tmp * 2;
				}
			}
			checkCode = (10 - (checkCode % 10)) % 10;
			console.log("check code", checkCode);
			return checkCode == +iva.charAt(10);
		}
		return false;
	}

	validationByCountry() {
		const vat = this.registrationForm.get("vatCode"),
			postalCode = this.registrationForm.get("postalCode"),
			fiscalCode = this.registrationForm.get("referenceFiscalCode"),
			birthDate = this.registrationForm.get("referenceBirthDate"),
			checkCCA = this.registrationForm.get("checkCCA"),
			checkDati = this.registrationForm.get("checkDati"),
			checkTec = this.registrationForm.get("checkTec"),
			region = this.registrationForm.get("region"),
			regon = this.registrationForm.get("regon"),
			visura = this.registrationForm.get("visura"),
			doc = this.registrationForm.get("doc");

		this.registrationForm.get("country").valueChanges.subscribe(country => {
			this.selectedCountry = country;

			switch (country) {
				case "IT":
					// ripristino le validator di default
					vat.setValidators(null);
					postalCode.setValidators(null);
					region.setValidators(null);
					regon.setValidators(null);
					fiscalCode.setValidators(null);
					birthDate.setValidators(null);
					birthDate.setValidators(this.validateDate);
					checkCCA.setValidators(null);
					checkDati.setValidators(null);
					checkTec.setValidators(null);

					vat.setValidators([
						Validators.required,
						Validators.maxLength(11),
						Validators.pattern("^([0-9]){11}$")
					]);
					postalCode.setValidators([
						Validators.maxLength(5),
						Validators.required,
						Validators.pattern("^([0-9]){5}$")
					]);
					fiscalCode.setValidators([
						Validators.maxLength(16),
						Validators.required,
						Validators.pattern(
							"^[a-zA-Z]{6}[0-9]{2}[a-zA-Z][0-9]{2}[a-zA-Z][0-9]{3}[a-zA-Z]$"
						)
					]);
					birthDate.setValidators([
						Validators.required,
						this.validateDate
					]);
					checkCCA.setValidators([Validators.requiredTrue]);
					checkDati.setValidators([Validators.requiredTrue]);
					checkTec.setValidators([Validators.requiredTrue]);
					visura.setValidators([Validators.required]);
					doc.setValidators([Validators.required]);

					region.setValidators(Validators.required);
					break;
				case "ES":
					visura.setValidators(null);
					doc.setValidators(null);
					// ripristino le validator di default
					vat.setValidators(null);
					postalCode.setValidators(null);
					region.setValidators(null);
					regon.setValidators(null);
					fiscalCode.setValidators(null);
					birthDate.setValidators(null);
					birthDate.setValidators(this.validateDate);
					checkCCA.setValidators(null);
					checkDati.setValidators(null);
					checkTec.setValidators(null);

					vat.setValidators([
						Validators.required,
						Validators.maxLength(9),
						Validators.pattern("^([A-Za-z0-9]){9}$")
					]);
					postalCode.setValidators([
						Validators.maxLength(5),
						Validators.required,
						Validators.pattern("^([0-9]){5}$")
					]);
					fiscalCode.setValidators([
						Validators.minLength(9),
						Validators.maxLength(9)
					]);
					region.setValidators(Validators.required);
					break;
				case "BE":
					// ripristino le validator di default
					visura.setValidators(null);
					doc.setValidators(null);
					vat.setValidators(null);
					postalCode.setValidators(null);
					region.setValidators(null);
					regon.setValidators(null);
					fiscalCode.setValidators(null);
					birthDate.setValidators(null);
					birthDate.setValidators(this.validateDate);
					checkCCA.setValidators(null);
					checkDati.setValidators(null);
					checkTec.setValidators(null);

					vat.setValidators([
						Validators.required,
						Validators.maxLength(10),
						Validators.pattern("^([0-9]){10}$")
					]);
					postalCode.setValidators([
						Validators.required,
						Validators.maxLength(4),
						Validators.pattern("^([0-9]){4}$")
					]);
					region.setValidators(Validators.required);
					break;
				case "FR":
					// ripristino le validator di default
					visura.setValidators(null);
					doc.setValidators(null);
					vat.setValidators(null);
					postalCode.setValidators(null);
					region.setValidators(null);
					regon.setValidators(null);
					fiscalCode.setValidators(null);
					birthDate.setValidators(null);
					birthDate.setValidators(this.validateDate);
					checkCCA.setValidators(null);
					checkDati.setValidators(null);
					checkTec.setValidators(null);

					vat.setValidators([
						Validators.required,
						Validators.maxLength(14),
						Validators.pattern("^([0-9]){14}$")
					]);
					postalCode.setValidators([
						Validators.maxLength(5),
						Validators.required,
						Validators.pattern("^([0-9]){5}$")
					]);
					region.setValidators(Validators.required);
					break;
				case "DE": 					// ripristino le validator di default
					visura.setValidators([Validators.required]);
					doc.setValidators(null);
					vat.setValidators(null);
					postalCode.setValidators([Validators.required]);
					region.setValidators(null);
					regon.setValidators(null);
					fiscalCode.setValidators(null);
					birthDate.setValidators(null);
					birthDate.setValidators(this.validateDate);
					checkCCA.setValidators(null);
					checkDati.setValidators(null);
					checkTec.setValidators(null);

					vat.setValidators([
						Validators.required,
						Validators.maxLength(11),
						Validators.pattern("^DE\\d{9}$")
					]);
					postalCode.setValidators([
						Validators.maxLength(5),
						Validators.pattern("^([0-9]){5}$")
					]);
					fiscalCode.setValidators([
						Validators.minLength(11),
						Validators.maxLength(11)
					]);
					region.setValidators(Validators.required);
					break;
				case "NL": 					// ripristino le validator di default
					visura.setValidators(null);
					doc.setValidators(null);
					vat.setValidators(null);
					postalCode.setValidators(null);
					region.setValidators(null);
					regon.setValidators(null);
					fiscalCode.setValidators(null);
					birthDate.setValidators(null);
					birthDate.setValidators(this.validateDate);
					checkCCA.setValidators(null);
					checkDati.setValidators(null);
					checkTec.setValidators(null);

					vat.setValidators([
						Validators.required,
						Validators.maxLength(30),
						Validators.required
					]);
					postalCode.setValidators([
						Validators.maxLength(10),
						Validators.required
					]);
					region.setValidators(Validators.required);
					break;
				case "EN":
					// ripristino le validator di default
					visura.setValidators(null);
					doc.setValidators(null);
					vat.setValidators(null);
					postalCode.setValidators(null);
					region.setValidators(null);
					regon.setValidators(null);
					fiscalCode.setValidators(null);
					birthDate.setValidators(null);
					birthDate.setValidators(this.validateDate);
					checkCCA.setValidators(null);
					checkDati.setValidators(null);
					checkTec.setValidators(null);

					vat.setValidators([
						Validators.required,
						Validators.maxLength(30)
					]);
					postalCode.setValidators([
						Validators.required,
						Validators.minLength(6),
						Validators.maxLength(9)
					]);
					region.setValidators(Validators.required);
					break;
				case "PL": 					// ripristino le validator di default
					visura.setValidators(null);
					doc.setValidators(null);
					vat.setValidators(null);
					postalCode.setValidators(null);
					region.setValidators(null);
					regon.setValidators(null);
					fiscalCode.setValidators(null);
					birthDate.setValidators(null);
					birthDate.setValidators(this.validateDate);
					checkCCA.setValidators(null);
					checkDati.setValidators(null);
					checkTec.setValidators(null);

					vat.setValidators([
						Validators.required,
						Validators.maxLength(10)
					]);
					postalCode.setValidators([
						Validators.required,
						Validators.minLength(6),
						Validators.maxLength(6)
					]);
					region.setValidators(Validators.required);
					regon.setValidators([
						Validators.minLength(9),
						Validators.maxLength(9)
					]);
					fiscalCode.setValidators([
						Validators.maxLength(11),
						Validators.required,
						Validators.minLength(11)
					]);
					break;
				case "PT":
					// ripristino le validator di default
					visura.setValidators(null);
					doc.setValidators(null);
					vat.setValidators(null);
					postalCode.setValidators(null);
					region.setValidators(null);
					regon.setValidators(null);
					fiscalCode.setValidators(null);
					birthDate.setValidators(null);
					birthDate.setValidators(this.validateDate);
					checkCCA.setValidators(null);
					checkDati.setValidators(null);
					checkTec.setValidators(null);

					vat.setValidators([
						Validators.required,
						Validators.minLength(9),
						Validators.maxLength(9)
					]);
					postalCode.setValidators([
						Validators.required,
						Validators.minLength(8),
						Validators.maxLength(8)
					]);
					break;
				case "LU":
					// ripristino le validator di default
					visura.setValidators(null);
					doc.setValidators(null);
					vat.setValidators(null);
					postalCode.setValidators(null);
					region.setValidators(null);
					regon.setValidators(null);
					fiscalCode.setValidators(null);
					birthDate.setValidators(null);
					birthDate.setValidators(null);
					checkCCA.setValidators(null);
					checkDati.setValidators(null);
					checkTec.setValidators(null);

					vat.setValidators([
						Validators.required,
						Validators.pattern("^LU\\d{8}$"),
						Validators.minLength(10),
						Validators.maxLength(10)
					]);
					break;
				default:
					// ripristino le validator di default
					visura.setValidators(null);
					doc.setValidators(null);
					vat.setValidators(null);
					postalCode.setValidators(null);
					region.setValidators(null);
					fiscalCode.setValidators(null);
					birthDate.setValidators(null);
					birthDate.setValidators(this.validateDate);
					checkCCA.setValidators(null);
					checkDati.setValidators(null);
					checkTec.setValidators(null);

					vat.setValidators([
						Validators.required,
						Validators.maxLength(15),
						Validators.pattern("^([A-Za-z0-9]){0,15}$")
					]);
					fiscalCode.setValidators([Validators.maxLength(20)]);
			}
		});
	}

	confirmEmail(event: Event, input: "email" | "confirm") {
		switch (input) {
			case "email":
				if (this.registrationForm.get("confEmail")?.value) {
					this.showErrEmailNotMatch =
						(event.target as HTMLInputElement).value !=
						this.registrationForm.get("confEmail").value;
				}
				break;
			case "confirm":
				if (this.registrationForm.get("email")?.value) {
					this.showErrEmailNotMatch =
						(event.target as HTMLInputElement).value !=
						this.registrationForm.get("email").value;
				}
			/* 	break;
			default:
				break; */
		}
	}

	sectionInvalid() {
		const details =
			this.registrationForm.get("businessName").valid &&
			this.registrationForm.get("address").valid &&
			this.registrationForm.get("country").valid &&
			this.registrationForm.get("email").valid &&
			this.registrationForm.get("confEmail").valid &&
			this.registrationForm.get("city").valid &&
			this.registrationForm.get("region").valid &&
			this.registrationForm.get("postalCode").valid &&
			this.registrationForm.get("vatCode").valid &&
			this.registrationForm.get("mobile").valid &&
			this.registrationForm.get("phone").valid &&
			this.registrationForm.get("regon").valid,

			docSection =
				this.registrationForm.get("visura").valid &&
				this.registrationForm.get("doc").valid,

			dealerSect =
				this.registrationForm.get("typeDealerActivity").valid &&
				this.registrationForm.get("carSold").valid &&
				this.registrationForm.get("buyingInterest").valid,

			intrnRefSect =
				this.registrationForm.get("referenceSurname").valid &&
				this.registrationForm.get("referenceName").valid &&
				this.registrationForm.get("referenceFiscalCode").valid &&
				this.registrationForm.get("referenceBirthDate").valid &&
				this.registrationForm.get("referenceEmail").valid &&
				this.registrationForm.get("referenceFCA").valid &&
				this.registrationForm.get("checkCCA").valid &&
				this.registrationForm.get("checkDati").valid &&
				this.registrationForm.get("checkTec").valid,

			consSect =
				this.registrationForm.get("checkConsents1").valid &&
				this.registrationForm.get("checkConsents2").valid &&
				this.registrationForm.get("checkConsents3").valid;

		if (!details || this.showErrEmailNotMatch) {
			document.getElementById("detailsSection")?.scrollIntoView(true);
		} else
			if (!dealerSect) {
				document.getElementById("dealerSection")?.scrollIntoView(true);
			} else
				if (!intrnRefSect) {
					document.getElementById("intrnlRfrncSection")?.scrollIntoView(true);
				} else
					if (!consSect) {
						document.getElementById("consentsSection")?.scrollIntoView(true);
					} else
						if (!docSection) {
							this.showErrorUpVisura = this.showErrorUpDoc = true;
							document.getElementById("docSection")?.scrollIntoView(true);
						} else {
							document.getElementById("detailsSection")?.scrollIntoView(true);
						}
	}

	private openDialog(errorCode: "1" | "2") {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.width = "auto";
		// dialogConfig.height = '250px';
		dialogConfig.data = new AlertMessage();
		dialogConfig.data.errorCode = errorCode;
		this.dialog.open(InformativeDialogComponent, dialogConfig);
	}
	openInformativa() {
		this.openDialog("1");
	}

	openTec() {
		this.openDialog("2");
	}

	uploadDoc(event: any) {
		console.log("upload doc", event);
		if (event[0]) {
			this.docLegalRefernce = event[0];
			this.registrationForm.controls.doc.setValue(event[0].name);
			this.showErrorUpDoc = false;
		} else {
			this.snackBarService.show({
				status: "error",
				titlePlaceholder: "error.600002",
				// messagePlaceholder: "dialog.success.title"
			});
		}
		if (event[1]) {
			this.docLegalRefernce2 = event[1];
			this.registrationForm.controls.doc2.setValue(event[1].name);
		}
	}

	uploadVisura(event: any) {
		if (event[0] && event[0].name.endsWith(".pdf")) {
			this.visuraCamerale = event[0];
			this.registrationForm.controls.visura.setValue(event[0].name);
			this.showErrorUpVisura = false;
		} else {
			this.snackBarService.show({
				status: "error",
				titlePlaceholder: "error.600002",
				// messagePlaceholder: "dialog.success.title"
			});
		}
	}

	deleteAttachmentDoc() {
		this.registrationForm.controls.doc.setValue(null);
		this.showErrorUpDoc = true;
		this.docLegalRefernce = this.docLegalRefernce2 = null;
	}

	deleteAttachmentDoc2() {
		this.registrationForm.controls.doc2.setValue(null);
		this.docLegalRefernce2 = null;
	}

	onFileChange(value: string) {
		switch (value) {
			case "visura":
				this.showErrorUpVisura = true;
				break;
			case "doc":
				this.showErrorUpDoc = true;
			/* 	break;
			default: */
		}
	}

	deleteAttachmentVisura() {
		this.registrationForm.controls.visura.setValue(null);
		this.showErrorUpVisura = true;
		this.visuraCamerale = null;
	}

	goBack() {
		this.location.back();
	}
}